/** CMS item from Contentful dynamicZoneComponentsCollection */
export interface LeadFormCmsItem {
  __typename: string;
  internalName: string;
  shortDescription: string | null;
  cardSectionTitle: string;
  longDescription?: string;
}

/** CMS page data from Contentful */
export interface LeadFormCmsPageData {
  pageTitle: string;
  pageName: string;
  urlSlug: string;
  topZoneComponentsCollection?: { items: LeadFormCmsItem[] };
  dynamicZoneComponentsCollection: { items: LeadFormCmsItem[] };
}

/** All possible field keys resolved from CMS shortDescription */
export type LeadFormFieldKey =
  | "enquiry"
  | "firstName"
  | "lastName"
  | "email"
  | "password"
  | "yearOfEntry"
  | "studyLevel"
  | "postcode"
  | "addressPostcode"
  | "openDaySelector"
  | "stayInKnow"
  | "uniNewsletter"
  | "countryOfResidence"
  | "addressBlock"
  | "unknown";

/** Form page types */
export type LeadFormPageType =
  | "requestinfo"
  | "opendays"
  | "digitalprospectus"
  | "physicalprospectus";

/** Interaction types sent to the BFF API */
export type InteractionType =
  | "REQUEST_INFO"
  | "GET_PROSPECTUS_DIGITAL"
  | "GET_PROSPECTUS_PHYSICAL"
  | "OPEN_DAY";

/** Form field values — all optional since CMS controls which fields appear */
export interface LeadFormValues {
  enquiry: string;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  yearOfEntry: string;
  studyLevel: string;
  postcode: string;
  openDayEventId: string;
  stayInKnow: boolean;
  uniNewsletter: boolean;
  countryOfResidence: string;
  addressLine1: string;
  addressLine2: string;
  townCity: string;
  addressPostcode: string;
  countrySelector: string;
}

/** Validation errors keyed by field */
export type LeadFormErrors = Partial<Record<LeadFormFieldKey, string>>;

/** Context props passed from the page — university/course details for submission */
export interface LeadFormSubmissionContext {
  collegeId: string;
  courseId?: string;
  institutionId?: string;
  collegeName?: string;
  courseName?: string;
  subOrderItemId?: string;
  sponsoredOrderItemId?: string;
  prospectusType?: string;
  interactivityId?: number;
  affiliateId?: number;
  providerEmailId?: string;
  profileId?: number;
  widgetId?: number;
  orderItemId?: number;
  odEventId?: number;
  qualificationInterest?: string;
  subjectInterest?: string;
  enquiryTypeId?: number;
  trackUserActionId?: number;
  institutionName?: string;
  institutionNameDisplay?: string;
  institutionTextKey?: string;
  institutionLogo?: string;
  institutionHeroImage?: string;
  overallRating?: number;
  overallRatingExact?: number;
  reviewCount?: number;
  clientConsentFlag?: string;
  clientConsentDate?: string;
  consentText?: string;
  requestUrl?: string;
  referrerUrl?: string;
}

/** Body payload for the interaction API */
export interface LeadFormInteractionBody {
  interactionType: string | null;
  interactionId: string | null;
  collegeId: number;
  courseId: number | null;
  interactivityId: number | null;
  dateSent: string | null;
  affiliateId: number | null;
  emailContent: string | null;
  providerEmailId: string | null;
  subOrderItemId: number | null;
  profileId: number | null;
  widgetId: number | null;
  postEnquiryFlag: string | null;
  mobileFlag: string | null;
  appFlag: string | null;
  clientConsentFlag: string | null;
  clientConsentDate: string | null;
  orderItemId: number | null;
  ipAddress: string | null;
  odEventId: number | null;
  qualificationInterest: string | null;
  subjectInterest: string | null;
  enquiryTypeId: number | null;
  trackUserActionId: number | null;
  createdAt: string | null;
  updatedAt: string | null;
  city: string | null;
  town: string | null;
  postCode: string | null;
  addressLineOne: string | null;
  addressLineTwo: string | null;
  country: string | null;
  requestUrl: string | null;
  referrerUrl: string | null;
  oneClickCheckboxFlag?: string | null;
  firstName: string | null;
  lastName: string | null;
  userEmail: string | null;
  inTakeYear: string | null;
  institutionName?: string | null;
  marketingEmailFlags?: string | null;
}

/** Interaction submission payload sent to BFF (request body) */
export type LeadFormInteractionPayload = LeadFormInteractionBody;

/** Props for individual field components */
export interface LeadFormFieldProps {
  cmsItem: LeadFormCmsItem;
  values: LeadFormValues;
  errors: LeadFormErrors;
  onChange: (field: keyof LeadFormValues, value: string | boolean) => void;
  clearError: (field: LeadFormFieldKey) => void;
}

/** Auth fields that are hidden when user is signed in */
export const AUTH_FIELD_KEYS: LeadFormFieldKey[] = [
  "firstName",
  "lastName",
  "email",
  "password",
];

/** Valid UK postcode regex */
export const UK_POSTCODE_REGEX =
  /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;

/** Valid email regex */
export const EMAIL_REGEX =
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Client consent item for bulk prospectus (one per university with clientConsentFlag === "Y") */
export interface ClientConsentItem {
  institutionId: string;
  institutionName: string;
  consentText: string;
}
